import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Sparkles, AlertTriangle, Calendar, Clock, MessageCircle, Users } from 'lucide-react';
import { HeroSection } from '../HeroSection';
import { projectCommunicationsService } from '../../services/projectCommunications';
import { toast } from 'sonner';

interface CommunicationPlan {
  id: string;
  /**
   * Numeric API id, present only for rows loaded from the backend.
   * DEMO rows leave this undefined — the Apply / Delete handlers
   * silently no-op for demo mode rather than attempt a 404 PATCH.
   */
  apiId?: number;
  stakeholder: string;
  informationRequired: string;
  frequency: string;
  format: string;
  owner: string;
  riskLevel?: 'High' | 'Medium' | 'Low';
  lastCommunication?: string;
  nextDue?: string;
}

const calculateNextDue = (lastComm: string, frequency: string): string => {
    const last = new Date(lastComm);
    switch (frequency) {
      case 'Daily':
        last.setDate(last.getDate() + 1);
        break;
      case 'Weekly':
        last.setDate(last.getDate() + 7);
        break;
      case 'Bi-weekly':
        last.setDate(last.getDate() + 14);
        break;
      case 'Monthly':
        last.setMonth(last.getMonth() + 1);
        break;
      case 'Quarterly':
        last.setMonth(last.getMonth() + 3);
        break;
      default:
        return 'N/A';
    }
    return last.toISOString().split('T')[0];
};

function formatDate(d: string | null): string {
  if (!d) return '';
  return d.slice(0, 10);
}

function mapApiToPlan(item: import('../../services/projectCommunications').ProjectCommunicationItem): CommunicationPlan {
  const lastComm = formatDate(item.last_communication);
  return {
    id: `CP-${String(item.id).padStart(3, '0')}`,
    apiId: item.id,
    stakeholder: item.stakeholder,
    informationRequired: item.information_required || '',
    frequency: item.frequency || '',
    format: item.format || '',
    owner: item.owner || '',
    riskLevel: (item.risk_level as 'High' | 'Medium' | 'Low') || undefined,
    lastCommunication: lastComm || undefined,
    nextDue: item.next_due ? formatDate(item.next_due) : (lastComm ? calculateNextDue(lastComm, item.frequency || '') : undefined),
  };
}

interface CommunicationPlanSectionProps {
  projectId?: number;
}

export function CommunicationPlanSection({ projectId }: CommunicationPlanSectionProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState<CommunicationPlan | null>(null);
  const [showAIInsights, setShowAIInsights] = useState(false);
  const [aiAnalyzing, setAIAnalyzing] = useState(false);
  const [plans, setPlans] = useState<CommunicationPlan[]>([]);

  const loadPlans = () => {
    if (!projectId) {
      setPlans([]);
      return;
    }
    projectCommunicationsService
      .list(projectId)
      .then((list) => setPlans(list.map(mapApiToPlan)))
      .catch(() => {
        toast.error('Failed to load communication plan');
        // Do NOT fall back to demo data on real projects — see RisksSection.
        setPlans([]);
      });
  };

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleAddPlan = () => {
    setEditingPlan(null);
    setShowForm(true);
  };

  const handleEditPlan = (plan: CommunicationPlan) => {
    setEditingPlan(plan);
    setShowForm(true);
  };

  const handleDeletePlan = async (plan: CommunicationPlan) => {
    // Sprint 20 — pre-fix this only mutated local state, so deletions
    // would "stick" until next reload. Now routed through the real
    // DELETE endpoint when the row originated from the backend.
    if (!projectId || !plan.apiId) {
      // Demo-mode row — drop locally, nothing to persist.
      setPlans((prev) => prev.filter((p) => p.id !== plan.id));
      return;
    }
    try {
      await projectCommunicationsService.delete(projectId, plan.apiId);
      toast.success('Communication plan removed');
      setPlans((prev) => prev.filter((p) => p.id !== plan.id));
    } catch {
      toast.error('Failed to delete plan');
    }
  };

  /**
   * Sprint 20 — apply an AI-suggested frequency change to the matching
   * plan row. Looks up by stakeholder name (stable identifier in the
   * UI) so the suggestion → row mapping survives sort/filter changes.
   */
  const handleApplyFrequencyChange = async (
    stakeholder: string,
    suggestedFrequency: string,
  ) => {
    if (!projectId) {
      toast.error('Save the project before applying suggestions');
      return;
    }
    const target = plans.find((p) => p.stakeholder === stakeholder);
    if (!target?.apiId) {
      toast.error('Could not find the matching plan to update');
      return;
    }
    try {
      await projectCommunicationsService.update(projectId, target.apiId, {
        frequency: suggestedFrequency,
      });
      toast.success(`Updated ${stakeholder} to ${suggestedFrequency.toLowerCase()} cadence`);
      loadPlans();
    } catch {
      toast.error('Failed to apply suggestion');
    }
  };

  const handleAIAnalysis = () => {
    setAIAnalyzing(true);
    setTimeout(() => {
      setAIAnalyzing(false);
      setShowAIInsights(true);
    }, 1500);
  };

  const getRiskColor = (level?: string) => {
    if (level === 'High') return 'bg-red-50 text-red-700 border-red-200';
    if (level === 'Medium') return 'bg-orange-50 text-orange-700 border-orange-200';
    return 'bg-blue-50 text-blue-700 border-blue-200';
  };

  const getDueDateStatus = (nextDue: string) => {
    const today = new Date('2024-11-30'); // Using the current date from system
    const due = new Date(nextDue);
    const diffDays = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { label: 'Overdue', color: 'bg-red-100 text-red-700 border-red-300' };
    if (diffDays === 0) return { label: 'Due Today', color: 'bg-orange-100 text-orange-700 border-orange-300' };
    if (diffDays <= 3) return { label: 'Due Soon', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' };
    return { label: 'On Track', color: 'bg-green-100 text-green-700 border-green-300' };
  };

  // Calculate metrics
  const highRiskCount = plans.filter(p => p.riskLevel === 'High').length;
  const overdueCount = plans.filter(p => {
    const status = getDueDateStatus(p.nextDue || '');
    return status.label === 'Overdue';
  }).length;

  // Real insights derived from the current plans list — replaces the legacy
  // hardcoded paragraph that named Jennifer Martinez / Lisa Anderson / Sarah
  // Thompson regardless of the actual data. Rule-based (no AI call needed):
  //   * riskUpdates: any High-risk plan with cadence less frequent than weekly.
  //   * conflicts: any frequency bucket with 2+ stakeholders.
  const FREQUENCY_RANK: Record<string, number> = {
    Daily: 1,
    Weekly: 2,
    'Bi-weekly': 3,
    Monthly: 4,
    Quarterly: 5,
    Annually: 6,
  };
  const aiIssues = (() => {
    const riskUpdates = plans
      .filter((p) => p.riskLevel === 'High')
      .filter((p) => (FREQUENCY_RANK[p.frequency] ?? 99) > FREQUENCY_RANK.Weekly)
      .map((p) => ({
        stakeholder: p.stakeholder,
        currentFrequency: p.frequency,
        suggestedFrequency: 'Weekly',
        reason: `High-risk stakeholder on ${p.frequency.toLowerCase()} cadence. Tighter loop reduces surprise.`,
        severity: 'High' as const,
      }));

    const byFreq = new Map<string, string[]>();
    for (const p of plans) {
      const k = p.frequency || 'Unspecified';
      const list = byFreq.get(k) ?? [];
      list.push(p.stakeholder);
      byFreq.set(k, list);
    }
    const conflicts = Array.from(byFreq.entries())
      .filter(([, stakeholders]) => stakeholders.length >= 2)
      .map(([freq, stakeholders]) => ({
        conflict: `${stakeholders.length} stakeholders share a ${freq.toLowerCase()} cadence`,
        stakeholders,
        suggestion: 'Consider a single combined update to reduce meeting overhead.',
        impact: `Saves ~${stakeholders.length - 1}× recurring meeting${stakeholders.length > 2 ? 's' : ''} per cycle.`,
      }));

    return { riskUpdates, conflicts };
  })();

  return (
    <div className="p-6 space-y-6">
      {/* Hero Section */}
      <HeroSection
        title="Communication Plan"
        description="Stakeholder communication planning and engagement tracking"
        variant="level4"
        metrics={[
          { label: 'Total Plans', value: plans.length.toString(), icon: MessageCircle, iconColor: 'bg-blue-50' },
          { label: 'High Risk', value: highRiskCount.toString(), icon: AlertTriangle, iconColor: 'bg-red-50' },
          { label: 'Overdue', value: overdueCount.toString(), icon: Clock, iconColor: 'bg-orange-50' },
          { label: 'Stakeholders', value: plans.length.toString(), icon: Users, iconColor: 'bg-purple-50' },
        ]}
        actionButton={{
          label: 'Add Plan',
          onClick: handleAddPlan,
          icon: <Plus className="w-4 h-4" />
        }}
      />

      {/* Action Bar */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={handleAIAnalysis}
            disabled={aiAnalyzing}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            <Sparkles className="w-4 h-4" />
            {aiAnalyzing ? 'Analyzing...' : 'AI Analysis'}
          </button>
        </div>
      </div>

      {/* AI Insights Panel */}
      {showAIInsights && (
        <div className="bg-white border border-purple-200 rounded-lg p-6 space-y-6">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl text-neutral-950">AI Communication Intelligence</h2>
          </div>

          {/* Risk-Based Update Suggestions */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <h3 className="text-lg text-neutral-950">Suggested Frequency Updates</h3>
              <span className="ml-auto text-xs text-gray-500">Based on stakeholder risk levels</span>
            </div>
            {aiIssues.riskUpdates.length === 0 && (
              <p className="text-sm text-gray-500 italic">No frequency upgrades suggested — high-risk stakeholders are already on weekly or tighter cadences.</p>
            )}
            <div className="space-y-3">
              {aiIssues.riskUpdates.map((update, idx) => (
                <div
                  key={idx}
                  className={`border rounded-lg p-4 ${
                    update.severity === 'High'
                      ? 'bg-red-50 border-red-200'
                      : 'bg-orange-50 border-orange-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm text-neutral-950">{update.stakeholder}</span>
                        <span
                          className={`px-2 py-0.5 rounded text-xs ${
                            update.severity === 'High'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-orange-100 text-orange-700'
                          }`}
                        >
                          {update.severity} Priority
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{update.reason}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-3 p-3 bg-white rounded border border-gray-200">
                    <div className="flex-1">
                      <div className="text-xs text-gray-500">Current</div>
                      <div className="text-sm text-neutral-950">{update.currentFrequency}</div>
                    </div>
                    <div className="text-xl text-gray-400">→</div>
                    <div className="flex-1">
                      <div className="text-xs text-gray-500">Suggested</div>
                      <div className="text-sm text-green-700">{update.suggestedFrequency}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleApplyFrequencyChange(update.stakeholder, update.suggestedFrequency)}
                      disabled={!projectId}
                      title={!projectId ? 'Save the project first' : undefined}
                      className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Conflicting Frequencies */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg text-neutral-950">Detected Frequency Conflicts</h3>
              <span className="ml-auto text-xs text-gray-500">Opportunities to optimize</span>
            </div>
            {aiIssues.conflicts.length === 0 && (
              <p className="text-sm text-gray-500 italic">No cadence overlaps detected — each stakeholder has a unique communication rhythm.</p>
            )}
            <div className="space-y-3">
              {aiIssues.conflicts.map((conflict, idx) => (
                <div key={idx} className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                  <div className="mb-2">
                    <div className="text-sm text-neutral-950 mb-1">{conflict.conflict}</div>
                    <div className="text-xs text-gray-600">
                      Affects: {conflict.stakeholders.join(', ')}
                    </div>
                  </div>
                  <div className="p-3 bg-white rounded border border-blue-200">
                    <div className="text-sm text-neutral-950 mb-1">
                      <strong>💡 Suggestion:</strong> {conflict.suggestion}
                    </div>
                    <div className="text-xs text-green-700">
                      <strong>Impact:</strong> {conflict.impact}
                    </div>
                  </div>
                  {/*
                    Sprint 20 — dropped the "Optimize" button. The
                    suggestion is advice-only (free-text, no single
                    canonical action — "merge stakeholders into one
                    update" requires user judgment about which plan
                    rows survive). A button that does nothing is worse
                    than no button; the call-to-action lives in the
                    suggestion text itself.
                  */}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Communication Plan Table - Compact */}
      <div className="bg-white rounded-lg border border-gray-200">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left py-3 px-4 text-xs text-gray-700 uppercase tracking-wide">ID</th>
              <th className="text-left py-3 px-4 text-xs text-gray-700 uppercase tracking-wide">Stakeholder</th>
              <th className="text-left py-3 px-4 text-xs text-gray-700 uppercase tracking-wide">Info Required</th>
              <th className="text-left py-3 px-4 text-xs text-gray-700 uppercase tracking-wide">Frequency</th>
              <th className="text-left py-3 px-4 text-xs text-gray-700 uppercase tracking-wide">Format</th>
              <th className="text-left py-3 px-4 text-xs text-gray-700 uppercase tracking-wide">Owner</th>
              <th className="text-left py-3 px-4 text-xs text-gray-700 uppercase tracking-wide">Next Due</th>
              <th className="text-left py-3 px-4 text-xs text-gray-700 uppercase tracking-wide">Risk</th>
              <th className="text-left py-3 px-4 text-xs text-gray-700 uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((plan) => {
              const dueStatus = plan.nextDue ? getDueDateStatus(plan.nextDue) : null;
              return (
                <tr key={plan.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <span className="text-xs font-mono text-gray-600">{plan.id}</span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-sm text-neutral-950">{plan.stakeholder}</div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-xs text-gray-600 max-w-[200px] truncate" title={plan.informationRequired}>
                      {plan.informationRequired}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs border border-blue-200">
                      {plan.frequency}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-xs text-gray-600">{plan.format}</div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-xs text-gray-700">{plan.owner}</div>
                  </td>
                  <td className="py-3 px-4">
                    {plan.nextDue && dueStatus ? (
                      <div className="flex items-center gap-2">
                        <Clock className="w-3 h-3 text-gray-500" />
                        <div>
                          <div className="text-xs text-gray-900">{plan.nextDue}</div>
                          <span className={`inline-block px-2 py-0.5 rounded text-xs border mt-1 ${dueStatus.color}`}>
                            {dueStatus.label}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs border ${getRiskColor(plan.riskLevel)}`}>
                      {plan.riskLevel || 'Low'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleEditPlan(plan)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Edit className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleDeletePlan(plan)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <CommunicationPlanForm
          plan={editingPlan}
          onClose={() => {
            setShowForm(false);
            setEditingPlan(null);
          }}
          onSave={(plan) => {
            if (editingPlan) {
              setPlans(plans.map((p) => (p.id === plan.id ? plan : p)));
            } else {
              setPlans([
                ...plans,
                { ...plan, id: `CP-${String(plans.length + 1).padStart(3, '0')}` },
              ]);
            }
            setShowForm(false);
            setEditingPlan(null);
          }}
        />
      )}
    </div>
  );
}

// Communication Plan Form Component
function CommunicationPlanForm({
  plan,
  onClose,
  onSave,
}: {
  plan: CommunicationPlan | null;
  onClose: () => void;
  onSave: (plan: CommunicationPlan) => void;
}) {
  const [formData, setFormData] = useState<CommunicationPlan>(
    plan || {
      id: '',
      stakeholder: '',
      informationRequired: '',
      frequency: '',
      format: '',
      owner: '',
      riskLevel: 'Medium',
      lastCommunication: '',
      nextDue: '',
    }
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl my-8">
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl text-neutral-950">
                {plan ? 'Edit Communication Plan' : 'Add Communication Plan'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Define communication requirements for stakeholders
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">
              ×
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Stakeholder */}
          <div>
            <label className="block text-sm mb-2 text-neutral-950">Stakeholder *</label>
            <input
              type="text"
              name="stakeholder"
              value={formData.stakeholder}
              onChange={handleChange}
              placeholder="Name and role of stakeholder or stakeholder group"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Information Required */}
          <div>
            <label className="block text-sm mb-2 text-neutral-950">Information Required *</label>
            <textarea
              name="informationRequired"
              value={formData.informationRequired}
              onChange={handleChange}
              rows={3}
              placeholder="What information does this stakeholder need?"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Frequency and Format */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm mb-2 text-neutral-950">Frequency *</label>
              <select
                name="frequency"
                value={formData.frequency}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select frequency</option>
                <option value="Daily">Daily</option>
                <option value="Weekly">Weekly</option>
                <option value="Bi-weekly">Bi-weekly</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Ad-hoc">Ad-hoc</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-2 text-neutral-950">Format *</label>
              <input
                type="text"
                name="format"
                value={formData.format}
                onChange={handleChange}
                placeholder="e.g., Email, Meeting, Dashboard"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          {/* Owner and Risk Level */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm mb-2 text-neutral-950">Owner *</label>
              <input
                type="text"
                name="owner"
                value={formData.owner}
                onChange={handleChange}
                placeholder="Who is responsible for this communication?"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm mb-2 text-neutral-950">Risk Level</label>
              <select
                name="riskLevel"
                value={formData.riskLevel}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                AI uses this to suggest frequency updates
              </p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {plan ? 'Update Plan' : 'Add Plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}